import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core'
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { TableModule } from 'primeng/table'
import { CertificateMetaDataForm, MetaDataRow } from '@features/certificate/models/certificate-meta-data/certificate-meta-data.model'
import { CertificateBaseComponent } from '@features/certificate/components/certificate-base/certificate-base.component'
import { startWith } from 'rxjs'
import { hasActualValue } from '@core/utils/form-utils'
import { formatDisplayDate } from '@core/utils/date-utils'
import { CERTIFICATE_CATEGORY_OPTIONS } from '@features/template/models/template.model'
import { COVERAGE_INSURANCE_TYPE_OPTIONS } from '@shared/models/coverage-insurance.model'
import { Tooltip } from 'primeng/tooltip'

@Component({
  selector: 'albert-certificate-meta-data',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    Tooltip
  ],
  templateUrl: './certificate-meta-data.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CertificateMetaDataComponent extends CertificateBaseComponent {
  public readonly form = input.required<FormGroup<CertificateMetaDataForm>>()
  private readonly formValues = signal<Partial<{ [K in keyof CertificateMetaDataForm]: CertificateMetaDataForm[K]['value'] }>>({})
  public readonly leftTableData = computed<MetaDataRow[]>(() => {
    const values = this.formValues()
    return [
      { label: 'Date of Creation', value: formatDisplayDate(values.certificatePublishedBy?.date) },
      { label: 'Created by', value: values.certificatePublishedBy?.name, tooltip: values.certificatePublishedBy?.email },
    ]
  })
  public readonly certificateCategoryLabel = computed<string | null>(() => {
    const value = this.formValues().certificateCategory
    if (!hasActualValue(value)) return null
    return CERTIFICATE_CATEGORY_OPTIONS.find(option => option.value === value)?.label ?? value
  })
  public readonly coverageTypeLabel = computed<string | null>(() => {
    const value = this.formValues().coverageType
    if (!hasActualValue(value)) return null
    return COVERAGE_INSURANCE_TYPE_OPTIONS.find(option => option.value === value)?.label ?? value
  })
  public readonly rightTableData = computed<MetaDataRow[]>(() => {
    const values = this.formValues()
    return [
      { label: 'Date of Change', value: formatDisplayDate(values.certificateChangedBy?.date) },
      { label: 'Changed by', value: values.certificateChangedBy?.name, tooltip: values.certificateChangedBy?.email }
    ]
  })

  constructor() {
    super()
    effect(() => {
      const form = this.form()
      const sub = form.valueChanges.pipe(startWith(form.value)).subscribe(val => {
        this.formValues.set(val)
      })
      return () => sub.unsubscribe()
    })
  }
}
